import * as React from "react"
import { RefObject } from "react"
import { CirclePicker } from 'react-color'
import trans from "trans"
import * as d3 from "d3"

import {Slider} from '@mui/material';

import {
    css,
    Theme,
    Grid,
    GridCell,
    Icon,
} from "@bdfx/mergado-ui-kit"

import {
    projectId,
    godProjectId,
    isGodModeOn
} from "app/helpers/api";

import {
    DELETE_KEY,
    DOWN_ARROW_KEY,
    LEFT_ARROW_KEY, MINUS_KEY,
    PLUS_KEY,
    RIGHT_ARROW_KEY,
    UP_ARROW_KEY
} from "app/helpers/keymap";
import { isJson } from "app/helpers/other";
import { CoordsType } from "shape-entities/components/shape";
import { iconDisabledColor } from "template-edit/components/tools-panel";

import { FadeLoader } from "react-spinners"
import { URL } from "app/config/api-config";
import { DrawDimensionLines, WatermarksRenderCanvasObjects } from "./render-objects-helper";
import { parseTransformAndScale } from "trans/helpers";
import { ZOOM_STEP } from "template-edit/containers/image-preview";
import { ID, intboolean } from "app/types/app-types";
import { NO_IMAGE_URL, stringToHash } from "shape-edit/forms/helpers";
import debounce from "lodash/debounce"
import { PositionType } from "products/types"

const MIN_ZOOM = .5
const MAX_ZOOM = 4

export type WatermarkMaskType = {
    id: number
    image_id: number
    product_id: number
    project_id: number
    group_id: number
    width: number
    height: number
    image_hash: string
    mask_hash: string
    user_set_objects: string
    created_at?: string
    updated_at?: string
}

export type AltWatermarkMaskType = {
    id: number
    alt_image_id: number
    alt_image_hash: string
    product_id: number
    template_id: number
    project_id: number
    group_id: number
    width: number
    height: number
    mask_hash: string
    user_set_objects: string
    created_at?: string
    updated_at?: string
}

const globalAlpha = 1
const globalCompositeOperation = 'source-over'              //source-over, xor
const globalCompositeOperationEraser = 'destination-out'    //destination-out

type MarkerTypes = 'inpainting-group' | 'brgremove-edit' | 'wmremove-manual' | 'wmremove-group-image'

interface Props {
    markerType: MarkerTypes
    product_id?: ID
    is_alt: boolean
    altLevel: number
    image_id?: ID
    image_hash?: string
    image_url: string
    image_url_hash: string
    image_width: number
    image_height: number
    group_id?: ID   //watermark / inpainting
    watermark_group_image_id?: ID
    watermark_group_image_filename?: string
    user_set_objects: string
    mask_hash: string
    closeModal: Function
    saveObjects: Function
    clearImageBlob: Function
    imageBlob: string
    isWaitingForResult: boolean
    showClose: boolean
    posRelativeTo?: PositionType
    scaleObjects?: intboolean
}

export type PaintLineType = {
    points: CoordsType[]
    radius: number
}

export type EraserLineType = {
    eraser: CoordsType[]
    radius: number
}

export type ObjectType = EraserLineType | PaintLineType
export type ObjectsType = ObjectType[]

export type UserDefinedObjectType = 'points' | 'eraser'

interface State {
    objectType: UserDefinedObjectType
    objects: ObjectsType
    activeLineIndex: number
    brushRadius: number
    color: string
    scale: number
    showColorPicker: boolean
    innerBox: DOMRect,
    outerBox: DOMRect,
    xoffset: number
    yoffset: number
}

const { abs, sin, cos, PI, max, min, round, atan2, atan, sqrt, floor, pow } = Math

class WatermarkMarkerCommon extends React.Component<Props, State> {
    canvasRef: RefObject<any>
    canvasWrapperRef: RefObject<any>
    backgroundCanvasRef: RefObject<any>
    zoom
    canvas: HTMLCanvasElement
    canvasWrapper: HTMLDivElement
    outerWrapper: HTMLDivElement

    constructor(props: Props) {
        super(props)

        this.canvasRef = React.createRef()
        this.canvasWrapperRef = React.createRef()
        this.backgroundCanvasRef = React.createRef()

        this.state = {
            objectType: 'points',
            activeLineIndex: 0,
            color: Theme.blue,
            scale: 1,
            showColorPicker: false,
            outerBox: null,
            innerBox: null,
            xoffset: null,
            yoffset: null,
            ...this.updateStateFromProps(props),
        }
    }

    updateStateFromProps = (props: Props) => {
        const { user_set_objects, image_width, image_height } = props
        const userSetObjects = user_set_objects && isJson(user_set_objects) ? JSON.parse(user_set_objects) : null
        const maxSide = max(image_width, image_height)
        return {
            objects: userSetObjects,
            brushRadius: round(maxSide / 40),
        }
    }

    getCanvas = () => {
        return this.canvasRef.current as HTMLCanvasElement
    }

    getBackgroundCanvas = () => {
        return this.backgroundCanvasRef.current as HTMLCanvasElement
    }

    getCanvasWrapper = () => {
        return this.canvasWrapperRef.current as HTMLDivElement
    }

    getOuterWrapper = () => {
        return document.getElementById('outer-wrapper') as HTMLDivElement
    }

    drawCanvasObjects = (objects: ObjectsType) => {
        const canvas = this.getCanvas()
        const backgroundCanvas = this.getBackgroundCanvas()
        WatermarksRenderCanvasObjects(objects, this.state.color, canvas, 1)
        this.updateCursor()

        if (this.props.posRelativeTo) {
            DrawDimensionLines(objects, this.state.color, backgroundCanvas, 1, this.props.posRelativeTo)
        } else {
            //clear dimension lines if exists
            const ctx = backgroundCanvas.getContext("2d");
            ctx.clearRect(0, 0, backgroundCanvas.width, backgroundCanvas.height);
        }
    }

    getNewPointPos = (event) => {
        const { scale, innerBox } = this.state
        return {
            newPointX: round((event.sourceEvent.clientX - innerBox.left) / scale),
            newPointY: round((event.sourceEvent.clientY - innerBox.top) / scale)
        }
    }

    dragStarted = (event, ctx) => {
        const { objects, brushRadius, color, objectType } = this.state
        const { newPointX, newPointY } = this.getNewPointPos(event)
        const isEraser = objectType === 'eraser'
        const alpha = isEraser ? 1 : globalAlpha

        ctx.globalCompositeOperation = isEraser ? globalCompositeOperationEraser : globalCompositeOperation;
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = color;
        ctx.fillStyle = color;

        ctx.beginPath()
        ctx.arc(newPointX, newPointY, brushRadius, 0, PI * 2, false);
        ctx.closePath();
        ctx.fill()

        ctx.beginPath()
        ctx.globalAlpha = alpha;
        ctx.moveTo(newPointX, newPointY);

        //add point to line
        const newPoint: CoordsType = {
            x: round(newPointX),
            y: round(newPointY),
        }

        //add point to line
        const newObject: PaintLineType | EraserLineType = objectType === 'points' ? {
            points: [newPoint],
            radius: brushRadius,
        } : {
            eraser: [newPoint],
            radius: brushRadius,
        }

        const updatedObjects = Array.isArray(objects) ? [...objects, newObject] : [newObject]
        this.setState({ activeLineIndex: updatedObjects.length - 1, objects: updatedObjects })
    }

    dragged = (event, ctx) => {
        const { objects, activeLineIndex, objectType, brushRadius } = this.state

        const { newPointX, newPointY } = this.getNewPointPos(event)
        const isEraser = objectType === 'eraser'
        const alpha = isEraser ? 1 : globalAlpha

        ctx.globalCompositeOperation = isEraser ? globalCompositeOperationEraser : globalCompositeOperation;
        ctx.globalAlpha = alpha;
        ctx.lineWidth = 2 * brushRadius;
        ctx.lineTo(newPointX, newPointY);
        ctx.stroke();

        //add point to line
        const newPoint: CoordsType = {
            x: round(newPointX),
            y: round(newPointY),
        }

        const updatedPoints = [...objects[activeLineIndex][objectType], newPoint]
        let updatedObjects = [...objects]
        updatedObjects[activeLineIndex][objectType] = updatedPoints

        this.setState({ objects: updatedObjects })
    }

    publishDeBounced = debounce(() => {
        this.handleSaveObjects(false)
    }, 200)

    dragEnded = (event, ctx) => {
        ctx.closePath()
        this.publishDeBounced()
    }

    handleRightClickOnCanvas = (event) => {
        event.preventDefault();
        d3.select(this.canvas).call(this.zoom.transform, d3.zoomIdentity);    //apply the transform  d3.zoomIdentity.translate(0,0).scale(1)
        this.setState({ brushRadius: round(max(this.canvas.clientWidth, this.canvas.clientHeight) / 40) })
        this.updateScaleAndBox()
    }

    getCircleCursor(r: number): string {
        const { color, objectType } = this.state
        const isEraser = objectType === 'eraser'
        const fillColor = isEraser ? '#ffffff0a' : color
        const lineColor = '#fff'

        const eraserSVGElementLine = (offset: number, lineColor: string) => `<line x1="${r / 2 + 1}" x2="${r + r / 2 + offset}" y1="${r + offset}" y2="${r + offset}" stroke="${lineColor}" stroke-width="1" fill="${lineColor}" /><line y1="${r / 2 + offset}" y2="${r + r / 2 + offset}" x1="${r + offset}" x2="${r + offset}" stroke="${lineColor}" stroke-width="1" fill="${fillColor}" />`
        const circle = (R: number, fill: string, stroke: string) => `<circle cx="${r}" cy="${r}" r="${R}" stroke="${stroke}" stroke-width="1" fill="${fill}" />`

        const circleEraser = circle(3, 'transparent', lineColor)
        const circleTwo = circle(r - 2, 'transparent', '#ffffff')
        const base64SVG = btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="${2 * r}px" height="${2 * r}px">${circle(r - 1, fillColor, Theme.steelblue)}${isEraser && (eraserSVGElementLine(0, '#fff') + eraserSVGElementLine(1, Theme.steelblue) + eraserSVGElementLine(-1, Theme.steelblue) + circleEraser)}${circleTwo}</svg>`);
        return `url('data:image/svg+xml;base64,${base64SVG}') ${r} ${r}, default`
    }

    updateScaleAndBox = () => {
        const { image_width, image_height } = this.props
        const { scale } = this.state

        const innerBox = this.canvas ? this.canvas.getBoundingClientRect() : null
        const outerBox = this.outerWrapper ? this.outerWrapper.getBoundingClientRect() : null

        const newScale = innerBox && innerBox.width ? innerBox.width / image_width : 1

        this.setState({
            innerBox: innerBox,
            outerBox: outerBox,
            scale: newScale,
        })

        if (scale !== newScale) {
            this.updateCursorDebounced()
        }
    }

    bindCanvas = () => {
        if (this.canvas && this.outerWrapper) {
            const xoffset = (this.outerWrapper.scrollWidth - this.canvas.scrollWidth) / 2
            const yoffset = (this.outerWrapper.scrollHeight - this.canvas.scrollHeight) / 2

            this.setState({
                xoffset: xoffset,
                yoffset: yoffset,
            })

            this.canvasWrapper.style.transformOrigin = `${xoffset}px 0px`

            const ctx = this.canvas.getContext("2d")

            let drag = d3.drag()
                /*.filter((event) => !event.button)   // Specify the filter (default includes !d3.event.ctrKey)*/
                .filter((event) => event.which === 1)
                .on("start", (event) => this.dragStarted(event, ctx))
                .on("drag", (event) => this.dragged(event, ctx))
                .on("end", (event) => this.dragEnded(event, ctx))

            this.zoom = d3.zoom()
                .extent([[0, 0], [this.canvas.clientWidth, this.canvas.clientHeight]]) //The viewport extent affects several functions: the center of the viewport remains fixed during changes by zoom.scaleBy and zoom.scaleTo; the viewport center and dimensions affect the path chosen by d3.interpolateZoom; and the viewport extent is needed to enforce the optional translate extent.
                .translateExtent([[0, 0], [this.canvas.clientWidth * 2, this.canvas.clientHeight]])   //The translate extent restricts panning, and may cause translation on zoom out.
                .scaleExtent([MIN_ZOOM, MAX_ZOOM])
                .on("zoom", this.handleZoom)
                .constrain((transform, extent, translateExtent) => {
                    const dx0 = transform.invertX(extent[0][0]) - translateExtent[0][0]
                    const dx1 = transform.invertX(extent[1][0]) - translateExtent[1][0]
                    const dy0 = transform.invertY(extent[0][1]) - translateExtent[0][1]
                    const dy1 = transform.invertY(extent[1][1]) - translateExtent[1][1]
                    return transform.translate(
                        dx1 > dx0 ? (dx0 + dx1) / 2 : min(0, dx0) || max(0, dx1),
                        dy1 > dy0 ? (dy0 + dy1) / 2 : min(0, dy0) || max(0, dy1)
                    );
                })

            d3.select(this.canvas)
                .on("contextmenu", (event) => {
                    this.handleRightClickOnCanvas(event)
                })
                .call(
                    drag
                )
                .call(
                    this.zoom
                )
        }
        this.drawCanvasObjects(this.state.objects)
    }

    getNormalizedPointerCoordinates = (zoomEvent): CoordsType => {
        if (zoomEvent.sourceEvent) {
            return {
                x: zoomEvent.sourceEvent.offsetX / this.canvas.clientWidth, //0-1
                y: zoomEvent.sourceEvent.offsetY / this.canvas.clientHeight //0-1
            }
        }
        return null
    }

    handleZoom = (event) => {
        const { xoffset, yoffset } = this.state
        if (this.canvasWrapper && event.transform.k && this.canvas) {
            if (event.sourceEvent) {
                /*const mouse = this.getNormalizedPointerCoordinates(event)
                const deltaX = mouse.x * (1 - event.transform.k) * this.canvas.scrollWidth
                const deltaY = mouse.y * (1 - event.transform.k) * this.canvas.scrollHeight*/

                //const overflows = this.detectOverflows()
                if (this.state.brushRadius * this.state.scale > 32) {
                    const newRadius = round(32 / this.state.scale)
                    this.setState({ brushRadius: newRadius >= 2 ? newRadius : 2 })
                }

                const deltaX = event.sourceEvent.offsetX * (1 - event.transform.k)
                const deltaY = event.sourceEvent.offsetY * (1 - event.transform.k)

                const currentTransform = parseTransformAndScale(this.canvasWrapper.style.translate)

                //const minY = -yoffset
                //const maxY = -yoffset+this.outerWrapper.clientHeight-event.transform.k*this.canvas.clientHeight
                //const minX = -xoffset
                //const maxX = -xoffset+this.outerWrapper.clientWidth-event.transform.k*this.canvas.clientWidth

                const newX = currentTransform.x + deltaX
                const newY = currentTransform.y + deltaY

                //const x = (overflows.includes('left') && !overflows.includes('right')) ? minX : ((overflows.includes('right') && !overflows.includes('left')) ? maxX : newX)
                //const y = (overflows.includes('top') && !overflows.includes('bottom')) ? minY : ((overflows.includes('bottom') && !overflows.includes('top')) ? maxY : newY)
                this.canvasWrapper.style.transform = `translate(${newX}px,${newY}px) scale(${event.transform.k})`
            } else {
                this.canvasWrapper.style.transform = `translate(${event.transform.x}px,${event.transform.y}px) scale(${event.transform.k})` //depends on translateExtent
            }
            this.updateScaleAndBox()
        }
    }

    updateCursor = (newScale: number = null) => {
        const { brushRadius, scale } = this.state
        if (this.canvas) {
            this.canvas.style.cursor = this.getCircleCursor(brushRadius * (newScale ? newScale : scale))
        }
    }

    updateCursorDebounced = debounce(() => {
        this.updateCursor()
    }, 200)

    updateScaleAndBoxDebounced = debounce(() => {
        this.updateScaleAndBox()
    }, 200)

    componentDidMount() {
        const { image_width, image_height } = this.props
        this.canvas = this.getCanvas()
        this.canvasWrapper = this.getCanvasWrapper()
        this.outerWrapper = this.getOuterWrapper()

        document.addEventListener("keydown", this.handleKeyDown)
        window.addEventListener('resize', this.updateScaleAndBox)

        const sectionContainer = document.getElementById('section-container')
        if (sectionContainer) {
            sectionContainer.addEventListener('scroll', this.updateScaleAndBox)
        }

        this.bindCanvas()

        if (this.outerWrapper) {
            const scaleX = this.outerWrapper.clientWidth / image_width
            const scaleY = this.outerWrapper.clientHeight / image_height


            if (scaleX > 1.2 && scaleY > 1.2) {
                const scale = min(scaleX, scaleY)
                const x = -.5 * (image_width * scale - image_width)
                const y = -.5 * (image_height * scale - image_height)
                this.canvasWrapper.style.transform = `translate(${x}px,${y}px) scale(${scale})`
            }
        }

        this.updateScaleAndBoxDebounced()
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (prevProps && (
            (this.props.image_hash && this.props.image_hash !== prevProps.image_hash) ||
            (this.props.group_id && this.props.group_id !== prevProps.group_id) ||
            (this.props.image_url && this.props.image_url !== prevProps.image_url) ||
            (this.props.image_id && this.props.image_id !== prevProps.image_id) ||
            (this.props.product_id && this.props.product_id !== prevProps.product_id) ||
            (this.props.user_set_objects !== prevProps.user_set_objects) ||
            (this.props.posRelativeTo !== prevProps.posRelativeTo)
        )) {
            if (this.props.image_url && this.props.image_url !== prevProps.image_url) {
                console.debug('about to clear image blob')
                this.props.clearImageBlob()
            }
            this.updateScaleAndBox()
            const updatedStateFromProps = this.updateStateFromProps(this.props)
            this.setState({ ...updatedStateFromProps })
            this.drawCanvasObjects(updatedStateFromProps.objects)
            if (this.props.user_set_objects) {
                this.publishDeBounced()
            }
            this.updateScaleAndBoxDebounced()
        }
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.handleKeyDown)
        window.removeEventListener('resize', this.updateScaleAndBox)
        const sectionContainer = document.getElementById('section-container')
        if (sectionContainer) {
            sectionContainer.removeEventListener('scroll', this.updateScaleAndBox)
        }
    }

    handleKeyDown = (event) => {
        const { objects } = this.state

        if (event.target.type || (event.target.tagName && event.target.tagName === 'INPUT')) {
            return;
        }

        if (event.ctrlKey) {
            switch (event.key) {
                case 's':   //save
                    event.preventDefault()
                    this.handleSaveObjects(false)
                    break;
            }
        } else {    //non-ctrlKeys:
            const step = 20
            let newZoomIdentity = d3.zoomIdentity
            if ([DOWN_ARROW_KEY, UP_ARROW_KEY, LEFT_ARROW_KEY, RIGHT_ARROW_KEY, PLUS_KEY, MINUS_KEY].includes(event.keyCode)) {
                event.preventDefault()
                const current = parseTransformAndScale(this.canvasWrapper.style.transform)
                switch (event.keyCode) {
                    case DOWN_ARROW_KEY:
                        newZoomIdentity = d3.zoomIdentity.translate(current.x, current.y + step).scale(current.scale)
                        break
                    case UP_ARROW_KEY:
                        newZoomIdentity = d3.zoomIdentity.translate(current.x, current.y - step).scale(current.scale)
                        break
                    case LEFT_ARROW_KEY:
                        newZoomIdentity = d3.zoomIdentity.translate(current.x - step, current.y).scale(current.scale)
                        break
                    case RIGHT_ARROW_KEY:
                        newZoomIdentity = d3.zoomIdentity.translate(current.x + step, current.y).scale(current.scale)
                        break
                    case PLUS_KEY:
                        newZoomIdentity = d3.zoomIdentity.translate(current.x, current.y).scale(current.scale + ZOOM_STEP)
                        break
                    case MINUS_KEY:
                        newZoomIdentity = d3.zoomIdentity.translate(current.x, current.y).scale(current.scale - ZOOM_STEP)
                        break
                }
                d3.select(this.canvas).call(this.zoom.transform, newZoomIdentity);

            } else {
                switch (event.keyCode) {
                    case DELETE_KEY:
                        event.preventDefault()
                        if (objects && Array.isArray(objects) && objects.length > 0) {
                            const updatedObjects = objects.slice(0, -1)
                            const updatedIndex = updatedObjects.length - 1
                            this.setState({
                                objects: updatedObjects,
                                activeLineIndex: updatedIndex > 0 ? updatedIndex : 0
                            })
                        }
                        break;
                    default:
                        //console.debug('other key', event)
                        break;
                }
            }
        }
    }

    saveObjects = (isCloseOnSave: boolean) => {
        const { objects } = this.state
        const { image_id, product_id, image_width, image_height, group_id, watermark_group_image_id, markerType, is_alt, altLevel } = this.props

        const png = this.canvas.toDataURL("image/png")

        const roundedPointsObjects : ObjectsType = objects && Array.isArray(objects) ? objects.map((object) => {
            return {
                ...object,
                points: typeof object['points'] !== 'undefined' && Array.isArray(object['points']) ? object['points'].map(point => {
                    return { x: round(point.x), y: round(point.y) }
                }) : object['points'],
                eraser: typeof object['eraser'] !== 'undefined' && Array.isArray(object['eraser']) ? object['eraser'].map(point => {
                    return { x: round(point.x), y: round(point.y) }
                }) : object['eraser'],
            }
        }) : null

        switch (markerType) {
            case 'wmremove-group-image':
                this.props.saveObjects(roundedPointsObjects, watermark_group_image_id, isCloseOnSave)
                break;
            case 'inpainting-group':
                this.props.saveObjects(roundedPointsObjects, png, isCloseOnSave)
                break;
            default:
                //saveWatermarkObjects, api.ts
                this.props.saveObjects(is_alt, image_id, altLevel, product_id, roundedPointsObjects, png, image_width, image_height, isCloseOnSave)
                break;
        }
    }

    handleSaveObjects = (isCloseOnSave: boolean) => {
        this.saveObjects(isCloseOnSave)
        if (isCloseOnSave) {
            this.props.closeModal()
        }
    }

    handleClearObjects = () => {
        this.props.clearImageBlob()
        this.setState({ objects: null, objectType: 'points', activeLineIndex: 0 })

        const ctx = this.canvas.getContext("2d")
        ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        const backgroundCanvas = this.getBackgroundCanvas()
        if (backgroundCanvas) {
            const ctxBg = backgroundCanvas.getContext("2d");
            ctxBg.clearRect(0, 0, backgroundCanvas.width, backgroundCanvas.height);
        }
    }

    handleChangeBrushRadius = (brushRadius: number) => {
        this.setState({ brushRadius: brushRadius })
        this.updateCursorDebounced()
    }

    detectOverflows = () => {
        const { innerBox, outerBox } = this.state
        let overflows = []

        if (!outerBox || !innerBox) {
            return null
        }
        if (innerBox.top < outerBox.top) {
            overflows.push('top')
        }
        if (innerBox.left < outerBox.left) {
            overflows.push('left')
        }
        if (innerBox.right > outerBox.right) {
            overflows.push('right')
        }
        if (innerBox.bottom > outerBox.bottom) {
            overflows.push('bottom')
        }

        return overflows
    }

    correctedTransform = (transform): { x: number, y: number } => {
        const { innerBox, outerBox } = this.state

        let x = null
        let y = null

        if (outerBox && innerBox) {
            if (innerBox.top < outerBox.top) {
                y = outerBox.top
            }
            if (innerBox.left < outerBox.left) {
                x = outerBox.left
            }
            if (innerBox.right > outerBox.right) {
                x = x ? (outerBox.left + outerBox.width / 2) : outerBox.right - innerBox.width
            }
            if (innerBox.bottom > outerBox.bottom) {
                y = y ? (outerBox.top + outerBox.height / 2) : outerBox.bottom - innerBox.width
            }
        }

        /*return {
            x: x ? transform.invertX(x) : transform.x,
            y: y ? transform.invertY(y) : transform.y
        }*/
        return {
            x: x,
            y: y,
        }
    }

    render() {
        const {image_url, image_url_hash, image_hash, image_width, image_height, user_set_objects, posRelativeTo, scaleObjects, mask_hash, imageBlob, watermark_group_image_filename, group_id, isWaitingForResult, showClose, markerType, is_alt } = this.props
        const { objects, brushRadius, color, objectType, showColorPicker } = this.state

        const userSetObjects = user_set_objects && isJson(user_set_objects) ? JSON.parse(user_set_objects) : null
        const isDiff = JSON.stringify(objects) !== JSON.stringify(userSetObjects)
        const iconSize = 28

        const t = stringToHash(JSON.stringify(objects) + mask_hash + imageBlob?.length)

        const position = `${posRelativeTo ? posRelativeTo : 'a'}_${scaleObjects ? 's' : 'k'}`;
        const actualProjectId = isGodModeOn ? godProjectId : projectId

        const altString = is_alt ? 'alt-' : ''
        const wmRemovedUrl = group_id ? markerType === "inpainting-group" && mask_hash && image_hash ?
            `${URL.laravelBase}get-inpainted-${altString}image/${actualProjectId}/${image_hash}/${mask_hash}/${position}?t=${t}` :
                watermark_group_image_filename && image_hash ? `${URL.laravelBase}get-wmgroup-image-inpainted/${actualProjectId}/${watermark_group_image_filename}/${image_hash}?t=${t}` :
                image_url_hash ? `${URL.laravelBase}get-${altString}wmremoved/${actualProjectId}/${image_url_hash}?t=${t}` : null
            : null

        const resultUrlContent = imageBlob ? imageBlob : ((objects && !isWaitingForResult && !isDiff) ? wmRemovedUrl : image_url)
        const resultUrl = `url("${resultUrlContent}")`

        if (resultUrlContent) {
            const img = new Image()
            img.src = resultUrlContent
            img.onerror = () => {
                console.debug('cannot load result image', image_hash, mask_hash, resultUrlContent, image_url, imageBlob)
            }
        }

        return <StyledComponentGrid className={'wm-common'}>
            <GridCell col={'1/-1'} style={{ textAlign: 'right' }}>
                {showClose &&
                    <StyledCloseSpan
                        onClick={() => {
                            isDiff ? this.handleSaveObjects(true) : this.props.closeModal()
                        }}>{trans('done')}</StyledCloseSpan>
                }
            </GridCell>

            <GridCell className={`canvas-wrapper`} id={'outer-wrapper'}>
                <div ref={this.canvasWrapperRef} className={`canvas-inner-wrapper`}>
                    <canvas
                        ref={this.backgroundCanvasRef}
                        className={`checker`}
                        width={image_width}
                        height={image_height}
                        style={{ backgroundImage: `url("${image_url}")`, position: 'absolute' }}
                    />
                    <canvas
                        ref={this.canvasRef}
                        className={`shadow-box`}
                        width={image_width}
                        height={image_height}
                        style={{ opacity: .75 }}
                        onLoad={() => this.updateCursor()}
                    />
                </div>
            </GridCell>

            <GridCell className={`centered canvas-wrapper`}>
                <div className={`canvas-inner-wrapper`}>
                    <canvas
                        className={`checker shadow-box`}
                        width={image_width}
                        height={image_height}
                        style={{
                            backgroundImage: resultUrl,
                            cursor: 'not-allowed',
                            position: 'relative'
                        }}
                    />

                    <StyledLoader>
                        <FadeLoader color={color} loading={isWaitingForResult} />
                    </StyledLoader>
                </div>
            </GridCell>

            <GridCell col={'1/-1'} style={{ zIndex: 9999, textAlign: 'center', alignSelf: 'end' }}>
                <StyledPanelGrid>
                    <GridCell />
                    <StyledCell style={{ background: objectType === 'points' ? '#ccebee' : 'transparent' }}>
                        <div
                            title={trans('brush')}
                            onClick={() => {
                                this.setState({ objectType: 'points' })
                                this.updateCursorDebounced()
                            }}>
                            <Icon type={'paint-brush'} size={iconSize} color={(objectType === 'points') ? Theme.steelblue : iconDisabledColor} />
                        </div>
                    </StyledCell>

                    <StyledCell style={{ background: objectType === 'eraser' ? '#ccebee' : 'transparent' }}>
                        <div
                            title={trans('eraser')}
                            onClick={() => {
                                this.setState({ objectType: 'eraser' })
                                this.updateCursorDebounced()
                            }}>
                            <Icon type={'eraser'} size={iconSize} color={(objectType === 'eraser') ? Theme.steelblue : iconDisabledColor} />
                        </div>
                    </StyledCell>

                    <StyledCell>
                        <div onClick={() => this.handleClearObjects()} title={trans('delete')}>
                            <Icon type={'trash'} size={iconSize} color={(objects && objects.length > 0) ? Theme.steelblue : iconDisabledColor} />
                        </div>
                    </StyledCell>

                    <GridCell style={{ paddingLeft: '8px', paddingRight: '8px', alignSelf: 'end' }}>
                        <div className={'slider-title'}>{trans('radius')} [px]</div>
                        <Slider
                            value={brushRadius}
                            onChange={(event, brushRadius: number) => this.handleChangeBrushRadius(brushRadius)}
                            defaultValue={10}
                            min={round(max(image_width, image_height) / 500)}
                            max={round(max(image_width, image_height) / 20)}
                            valueLabelDisplay="auto"
                        />
                    </GridCell>

                    <StyledColorPicker title={trans('brush_color')}>
                        <div style={{ position: 'relative', top: '-45px', left: '-5px', height: '40px', }}>&nbsp;
                            {showColorPicker &&
                                <CirclePicker
                                    color={color}
                                    colors={[Theme.blue, '#ffffff', '#385499', Theme.yellow, Theme.green]}
                                    onChangeComplete={(color) => {
                                        this.setState({ color: color.hex, showColorPicker: false, objectType: 'points' })
                                        this.drawCanvasObjects(objects)
                                    }}
                                />
                            }
                        </div>

                        <div onClick={() => this.setState({ showColorPicker: !showColorPicker })}
                            style={{
                                position: 'relative',
                                top: '-8px',
                                width: '24px',
                                height: '24px',
                                background: color,
                                borderRadius: '12px',
                                border: '1px solid gray',
                                cursor: 'pointer',
                                boxShadow: '0 0 3px 1px rgba(0,0,0,.3)',
                            }}>&nbsp;</div>
                    </StyledColorPicker>

                    <GridCell>
                        {/* do not show preview: FIE-656 */}
                        {/* {objects && wMaskUrl &&
                            <a href={wMaskUrl} target={'_blank'} title={'mask'}>
                                <img
                                    key={`wm${t}`}
                                    src={wMaskUrl}
                                    className={'preview-img'}
                                    onError={(e) => {
                                        if (e.currentTarget.src !== NO_IMAGE_URL) {
                                            e.currentTarget.src = NO_IMAGE_URL
                                        }
                                    }}
                                />
                            </a>
                        }
                        {wMaskedImageUrl &&
                            <a href={wMaskedImageUrl} target={'_blank'} title={'masked'}>
                                <img
                                    key={`mi${t}`}
                                    src={wMaskedImageUrl}
                                    className={'preview-img'}
                                    onError={(e) => {
                                        if (e.currentTarget.src !== NO_IMAGE_URL) {
                                            e.currentTarget.src = NO_IMAGE_URL
                                        }
                                    }}
                                />
                            </a>
                        } */}
                    </GridCell>
                </StyledPanelGrid>
            </GridCell>
        </StyledComponentGrid>
    }
}

const StyledCloseSpan = css('span')({
    position: 'absolute',
    fontSize: '22px',
    zIndex: 99999,
    cursor: 'pointer',
    right: '20px',
    top: '20px',
    color: Theme.blue
})

const StyledComponentGrid = css(Grid)({
    /* '@media screen and (min-width: 800px)': {
        minWidth: '800px',
    },
    '@media screen and (min-width: 1024px)': {
        minWidth: '1024px',
    }, */   //TODO: not for iframe!!!
    /* height: '98%', */
    gridTemplateColumns: '50% 50%',
    gridTemplateRows: 'auto max-content',
    ' .canvas-wrapper': {
        alignContent: 'center',
        textAlign: 'center',
        position: 'relative',
        padding: '1px',
        display: 'grid',
        minHeight: 'calc(100vh - 400px)',
        /* height: 'calc(100vh - 240px)', */    /* TODO: TEST */
    },
    ' .canvas-inner-wrapper': {
        alignSelf: 'center',
        padding: '1px',
    },
    ' canvas': {
        backgroundSize: 'cover',
        maxWidth: 'calc(100% - 40px)',
        maxHeight: 'calc(100vh - 250px)',
    },
    ' .centered': {
        alignSelf: 'center',
        textAlign: 'center',
    },
    ' .slider-title': {
        color: Theme.steelblue,
        fontSize: '12px',
    }
})

const StyledColorPicker = css(GridCell)({
    alignSelf: 'center',
    ' .circle-picker': {
        marginTop: '20px',
        paddingLeft: '5px',
        'span div': {
            marginBottom: 0,
        },
        'span div span div': {
            border: '1px solid gray',
        },
    },
})

const StyledPanelGrid = css(Grid)({
    gridTemplateColumns: "auto 40px 40px 40px 200px 100px auto",
    paddingLeft: '10px',
    borderRadius: '3px solid gray',
    textAlign: 'center',
    alignSelf: 'center',
    ' img.preview-img': {
        maxWidth: '60px',
        maxHeight: '60px',
    },
    '@media screen and (max-width: 1024px)': {
        gridTemplateColumns: "auto 40px 40px 40px max-content 100px auto",
    }
})

const StyledCell = css(GridCell)({
    position: 'relative',
    width: '40px',
    overflow: 'hidden',
    textAlign: 'center',
    alignSelf: 'end',
    cursor: 'pointer',
    borderRadius: '3px',
    ' button': {
        width: '100%',
        height: '100%',
        ':active': {
            background: 'transparent',
        },
        ':focus': {
            background: 'transparent',
        },
    },
})

const StyledLoader = css('div')({
    '&> span': {
        position: 'absolute !important',
        top: 'calc(50% - 10px) !important',
    }
})

export default WatermarkMarkerCommon
